import { stringToBoolean } from '@mvecom/common/utils/stringToBoolean';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import Cookies from 'js-cookie';

import { Actions } from '../actions/enums';
import { getEnv } from '../utils/env';
import { getAuthTokenCookieName } from '../utils/getAuthCookieName';
import { setInsiderObject } from '../utils/insiders';
import { isCiamEnabled } from '../utils/retailUnitsWithCiam';
import { getCart } from './endpoints/cart/getCart';
import { getSession } from './endpoints/getSession';
import { getAuth0Session } from './endpoints/profile-pages/getAuth0Session';
import { Cookie } from './enums';
import { NetworkError as IdentityNetworkError } from './identityProvider/types';
import { RequestTimeout, SessionContextResponse } from './types';
import { backendUrl } from './utils/getBackendUrl';
import { getCookieDomain } from './utils/getCookieDomain';
import { getRetailUnitAndLanguage } from './utils/getRetailUnitAndLanguage';
import { getRetailUnitCookiePath } from './utils/getRetailUnitCookiePath';

const AXIOS_RETRIES = 3;

class CustomAxiosError extends Error {
  constructor(
    public message: string,
    public type: string,
    public traceId: string,
    public status?: number,
  ) {
    super(message);
    Object.setPrototypeOf(this, CustomAxiosError.prototype);
  }
}

const handleAxiosError = (error: AxiosError<BackendErrorResponse>): never => {
  const { response, code } = error;
  const status = response?.status;
  const message = response?.data?.error?.message ?? `Error status: ${status}`;
  const type = response?.data?.error?.type ?? code ?? 'unknown';
  const traceId = response?.data?.error?.traceId ?? '';

  throw new CustomAxiosError(message, type, traceId, status);
};

export interface BackendErrorResponse {
  status: number;
  error: {
    type: string;
    message: string;
    traceId: string;
  };
}

enum NetworkError {
  CONNECTION_ABORTED = 'ECONNABORTED',
}

const setCookies = (session: SessionContextResponse) => {
  if (session.userType === 'G') {
    Cookies.set(Cookie.FAVOURITES_GUEST_ID, session.userId, {
      domain: getCookieDomain,
      path: getRetailUnitCookiePath,
    });
    Cookies.set(Cookie.CART_GUEST_ID, session.userId, { domain: getCookieDomain, path: getRetailUnitCookiePath });
    Cookies.set(Cookie.USER_LOGGEDIN, 'false', { domain: getCookieDomain, path: getRetailUnitCookiePath });
    Cookies.remove(Cookie.IKEA_SESSION, { domain: getCookieDomain, path: getRetailUnitCookiePath });
  } else if (!stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN))) {
    Cookies.set(Cookie.USER_LOGGEDIN, 'true', { domain: getCookieDomain, path: getRetailUnitCookiePath });
    globalThis?.ikea?.pubsub?.publish?.(Actions.USER_INFO_AVAILABLE);
  }
};

export const setupAuth0Session = async () => {
  const sessionStatus = await getAuth0Session();

  if (sessionStatus.isAuthenticated && !stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN))) {
    Cookies.set(Cookie.USER_LOGGEDIN, 'true', { domain: getCookieDomain, path: getRetailUnitCookiePath });
    globalThis?.ikea?.pubsub?.publish?.(Actions.USER_INFO_AVAILABLE);
  }

  return sessionStatus;
};

export const setupSession = async () => {
  const session = await getSession();
  setCookies(session);
  setInsiderObject(session);
  return {
    isLoggedIn: session.userType === 'R',
  };
};

export const axiosRetryDefault = async (error: AxiosError<BackendErrorResponse>) => {
  if (
    error.response?.data.error.type === IdentityNetworkError.IRW_COOKIE_MISSING ||
    error.response?.data.error.type === IdentityNetworkError.NO_ACCESS
  ) {
    await setupSession();
    return true;
  }
  if (
    error.response?.data.error.type === IdentityNetworkError.CART_NOT_FOUND ||
    error.response?.data.error.type === IdentityNetworkError.LINE_NOT_FOUND
  ) {
    await setupSession();
    await getCart({ queryParams: { skipSync: true, includePrices: false } });
    return true;
  }
  if (error.code === NetworkError.CONNECTION_ABORTED) return true;
  if ((error.response?.status as number) >= 500) return true;
  return false;
};

export const cartAxiosInstance = ((): AxiosInstance => {
  const cartAxios = axios.create({
    baseURL: backendUrl(),
    timeout: RequestTimeout.XXL,
    withCredentials: true,
  });

  axiosRetry(cartAxios, {
    retries: AXIOS_RETRIES,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError<any>) => axiosRetryDefault(error),
  });

  cartAxios.interceptors.request.use(
    (config) => config,
    (error: AxiosError) => Promise.reject(error),
  );
  cartAxios.interceptors.response.use(
    (res: AxiosResponse) => res,
    (error: AxiosError<BackendErrorResponse>) => handleAxiosError(error),
  );

  return cartAxios;
})();

export const sessionAxiosInstance = ((): AxiosInstance => {
  const sessionAxios = axios.create({
    baseURL: backendUrl(),
    timeout: RequestTimeout.XXL,
    withCredentials: true,
  });

  axiosRetry(sessionAxios, {
    retries: AXIOS_RETRIES,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError<any>) => axiosRetryDefault(error),
  });

  sessionAxios.interceptors.request.use(
    (config) => config,
    (error: AxiosError) => Promise.reject(error),
  );
  sessionAxios.interceptors.response.use(
    (res: AxiosResponse) => res,
    (error: AxiosError<BackendErrorResponse>) => handleAxiosError(error),
  );

  return sessionAxios;
})();

export const favouritesAxiosInstance = ((): AxiosInstance => {
  const favouritesAxios = axios.create({
    baseURL: backendUrl(),
    timeout: RequestTimeout.XXL,
    withCredentials: true,
  });

  axiosRetry(favouritesAxios, {
    retries: AXIOS_RETRIES,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError<any>) => axiosRetryDefault(error),
  });

  favouritesAxios.interceptors.request.use(
    (config) => config,
    (error: AxiosError) => Promise.reject(error),
  );
  favouritesAxios.interceptors.response.use(
    (res: AxiosResponse) => res,
    (error: AxiosError<BackendErrorResponse>) => handleAxiosError(error),
  );

  return favouritesAxios;
})();

export const profileAxiosInstance = ((): AxiosInstance => {
  const profileAxios = axios.create({
    baseURL: backendUrl(),
    timeout: RequestTimeout.XXL,
    withCredentials: true,
  });

  axiosRetry(profileAxios, {
    retries: AXIOS_RETRIES,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError<any>) => axiosRetryDefault(error),
  });

  if (isCiamEnabled) {
    profileAxios.interceptors.request.use((config) => {
      const { retailUnit } = getRetailUnitAndLanguage();
      const token = Cookies.get(getAuthTokenCookieName(retailUnit));
      if (token) config.headers.set('Authorization', `Bearer ${token}`);
      return config;
    });
  }

  return profileAxios;
})();

export const authAxiosInstance = ((): AxiosInstance => {
  const baseURL = (() => {
    const { retailUnit, language } = getRetailUnitAndLanguage();
    const basePath = `/${retailUnit}/${language}`;
    const env = getEnv();
    if (env === 'qa') {
      return `https://www.cte.ikeadt.com${basePath}`;
    } else if (env === 'production') {
      return `https://ikea.marat.qd.je${basePath}`;
    }
    return `${globalThis.location.origin}${basePath}`;
  })();

  const authAxios = axios.create({
    baseURL,
    timeout: RequestTimeout.XXL,
    withCredentials: true,
  });

  axiosRetry(authAxios, {
    retries: AXIOS_RETRIES,
    shouldResetTimeout: true,
    retryCondition: (error: AxiosError<any>) => axiosRetryDefault(error),
  });

  return authAxios;
})();
